import React, { useState, useEffect } from 'react'
import { supabase } from '../../supabaseClient'

function ManageBrands() {
  // State for brands
  const [brands, setBrands] = useState([])
  const [showBrandForm, setShowBrandForm] = useState(false)
  const [editingBrand, setEditingBrand] = useState(null)
  const [brandFormData, setBrandFormData] = useState({
    brand_name: '',
    website_url: '',
    country: '',
    description: ''
  })

  // State for lure types
  const [lureTypes, setLureTypes] = useState([])
  const [showTypeForm, setShowTypeForm] = useState(false)
  const [editingType, setEditingType] = useState(null)
  const [typeFormData, setTypeFormData] = useState({
    type_name: '',
    description: ''
  })

  // State for lure subtypes
  const [lureSubtypes, setLureSubtypes] = useState([])
  const [showSubtypeForm, setShowSubtypeForm] = useState(false)
  const [editingSubtype, setEditingSubtype] = useState(null)
  const [subtypeFormData, setSubtypeFormData] = useState({
    subtype_name: '',
    description: ''
  })

  // UI state
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [success, setSuccess] = useState('')

  useEffect(() => {
    fetchAllData()
  }, [])

  const fetchAllData = async () => {
    try {
      // Fetch brands
      const { data: brandsData } = await supabase
        .from('brands')
        .select('*')
        .order('brand_name')

      // Fetch lure types
      const { data: typesData } = await supabase
        .from('lure_types')
        .select('*')
        .order('type_name')

      // Fetch lure subtypes
      const { data: subtypesData } = await supabase
        .from('lure_subtypes')
        .select('*')
        .order('subtype_name')

      setBrands(brandsData || [])
      setLureTypes(typesData || [])
      setLureSubtypes(subtypesData || [])
      setLoading(false)
    } catch (err) {
      console.error('Error fetching data:', err)
      setError('Failed to load data')
      setLoading(false)
    }
  }

  // ============================================
  // BRAND HANDLERS
  // ============================================

  const handleBrandFormChange = (e) => {
    const { name, value } = e.target
    setBrandFormData(prev => ({
      ...prev,
      [name]: value
    }))
  }

  const handleAddBrand = () => {
    setBrandFormData({
      brand_name: '',
      website_url: '',
      country: '',
      description: ''
    })
    setShowBrandForm(true)
    setEditingBrand(null)
    setError('')
    setSuccess('')
  }

  const handleEditBrand = (brand) => {
    setBrandFormData({
      brand_name: brand.brand_name,
      website_url: brand.website_url || '',
      country: brand.country || '',
      description: brand.description || ''
    })
    setEditingBrand(brand)
    setShowBrandForm(true)
    setError('')
    setSuccess('')
  }

  const handleSubmitBrand = async (e) => {
    e.preventDefault()
    setError('')
    setSuccess('')

    if (!brandFormData.brand_name.trim()) {
      setError('Brand name is required')
      return
    }

    try {
      if (editingBrand) {
        // Update
        const { error: updateError } = await supabase
          .from('brands')
          .update({
            brand_name: brandFormData.brand_name.trim(),
            website_url: brandFormData.website_url.trim() || null,
            country: brandFormData.country.trim() || null,
            description: brandFormData.description.trim() || null
          })
          .eq('brand_id', editingBrand.brand_id)

        if (updateError) throw updateError
        setSuccess('Brand updated successfully!')
      } else {
        // Insert
        const { error: insertError } = await supabase
          .from('brands')
          .insert([{
            brand_name: brandFormData.brand_name.trim(),
            website_url: brandFormData.website_url.trim() || null,
            country: brandFormData.country.trim() || null,
            description: brandFormData.description.trim() || null
          }])

        if (insertError) throw insertError
        setSuccess('Brand added successfully!')
      }

      fetchAllData()
      setShowBrandForm(false)
      setEditingBrand(null)
      setTimeout(() => setSuccess(''), 3000)

    } catch (err) {
      console.error('Error saving brand:', err)
      if (err.code === '23505') {
        setError('This brand name already exists')
      } else {
        setError(err.message || 'Failed to save brand')
      }
    }
  }

  const handleDeleteBrand = async (brand) => {
    if (!window.confirm(`Delete "${brand.brand_name}"? This will affect all lures from this brand.`)) {
      return
    }

    try {
      // Check if brand has lures
      const { count: lureCount } = await supabase
        .from('base_lures')
        .select('*', { count: 'exact', head: true })
        .eq('brand_id', brand.brand_id)

      // Check if brand has colors
      const { count: colorCount } = await supabase
        .from('colors')
        .select('*', { count: 'exact', head: true })
        .eq('brand_id', brand.brand_id)

      if (lureCount > 0 || colorCount > 0) {
        setError(`Cannot delete "${brand.brand_name}" - it has ${lureCount} lure(s) and ${colorCount} color(s)`)
        return
      }

      const { error: deleteError } = await supabase
        .from('brands')
        .delete()
        .eq('brand_id', brand.brand_id)

      if (deleteError) throw deleteError

      setSuccess('Brand deleted successfully!')
      fetchAllData()
      setTimeout(() => setSuccess(''), 3000)

    } catch (err) {
      console.error('Error deleting brand:', err)
      setError(err.message || 'Failed to delete brand')
    }
  }

  // ============================================
  // LURE TYPE HANDLERS
  // ============================================

  const handleTypeFormChange = (e) => {
    const { name, value } = e.target
    setTypeFormData(prev => ({
      ...prev,
      [name]: value
    }))
  }

  const handleAddType = () => {
    setTypeFormData({
      type_name: '',
      description: ''
    })
    setShowTypeForm(true)
    setEditingType(null)
    setError('')
    setSuccess('')
  }

  const handleEditType = (type) => {
    setTypeFormData({
      type_name: type.type_name,
      description: type.description || ''
    })
    setEditingType(type)
    setShowTypeForm(true)
    setError('')
    setSuccess('')
  }

  const handleSubmitType = async (e) => {
    e.preventDefault()
    setError('')
    setSuccess('')

    if (!typeFormData.type_name.trim()) {
      setError('Type name is required')
      return
    }

    try {
      if (editingType) {
        const { error: updateError } = await supabase
          .from('lure_types')
          .update({
            type_name: typeFormData.type_name.trim(),
            description: typeFormData.description.trim() || null
          })
          .eq('type_id', editingType.type_id)

        if (updateError) throw updateError
        setSuccess('Lure type updated successfully!')
      } else {
        const { error: insertError } = await supabase
          .from('lure_types')
          .insert([{
            type_name: typeFormData.type_name.trim(),
            description: typeFormData.description.trim() || null
          }])

        if (insertError) throw insertError
        setSuccess('Lure type added successfully!')
      }

      fetchAllData()
      setShowTypeForm(false)
      setEditingType(null)
      setTimeout(() => setSuccess(''), 3000)

    } catch (err) {
      console.error('Error saving type:', err)
      setError(err.message || 'Failed to save lure type')
    }
  }

  const handleDeleteType = async (type) => {
    if (!window.confirm(`Delete lure type "${type.type_name}"?`)) {
      return
    }

    try {
      const { count } = await supabase
        .from('base_lures')
        .select('*', { count: 'exact', head: true })
        .eq('type_id', type.type_id)

      if (count > 0) {
        setError(`Cannot delete "${type.type_name}" - it's used by ${count} lure(s)`)
        return
      }

      const { error: deleteError } = await supabase
        .from('lure_types')
        .delete()
        .eq('type_id', type.type_id)

      if (deleteError) throw deleteError

      setSuccess('Lure type deleted successfully!')
      fetchAllData()
      setTimeout(() => setSuccess(''), 3000)

    } catch (err) {
      console.error('Error deleting type:', err)
      setError(err.message || 'Failed to delete lure type')
    }
  }

  // ============================================
  // LURE SUBTYPE HANDLERS
  // ============================================

  const handleSubtypeFormChange = (e) => {
    const { name, value } = e.target
    setSubtypeFormData(prev => ({
      ...prev,
      [name]: value
    }))
  }

  const handleAddSubtype = () => {
    setSubtypeFormData({
      subtype_name: '',
      description: ''
    })
    setShowSubtypeForm(true)
    setEditingSubtype(null)
    setError('')
    setSuccess('')
  }

  const handleEditSubtype = (subtype) => {
    setSubtypeFormData({
      subtype_name: subtype.subtype_name,
      description: subtype.description || ''
    })
    setEditingSubtype(subtype)
    setShowSubtypeForm(true)
    setError('')
    setSuccess('')
  }

  const handleSubmitSubtype = async (e) => {
    e.preventDefault()
    setError('')
    setSuccess('')

    if (!subtypeFormData.subtype_name.trim()) {
      setError('Subtype name is required')
      return
    }

    try {
      if (editingSubtype) {
        const { error: updateError } = await supabase
          .from('lure_subtypes')
          .update({
            subtype_name: subtypeFormData.subtype_name.trim(),
            description: subtypeFormData.description.trim() || null
          })
          .eq('subtype_id', editingSubtype.subtype_id)

        if (updateError) throw updateError
        setSuccess('Subtype updated successfully!')
      } else {
        const { error: insertError } = await supabase
          .from('lure_subtypes')
          .insert([{
            subtype_name: subtypeFormData.subtype_name.trim(),
            description: subtypeFormData.description.trim() || null
          }])

        if (insertError) throw insertError
        setSuccess('Subtype added successfully!')
      }

      fetchAllData()
      setShowSubtypeForm(false)
      setEditingSubtype(null)
      setTimeout(() => setSuccess(''), 3000)

    } catch (err) {
      console.error('Error saving subtype:', err)
      setError(err.message || 'Failed to save subtype')
    }
  }

  const handleDeleteSubtype = async (subtype) => {
    if (!window.confirm(`Delete subtype "${subtype.subtype_name}"?`)) {
      return
    }

    try {
      const { error: deleteError } = await supabase
        .from('lure_subtypes')
        .delete()
        .eq('subtype_id', subtype.subtype_id)

      if (deleteError) throw deleteError

      setSuccess('Subtype deleted successfully!')
      fetchAllData()
      setTimeout(() => setSuccess(''), 3000)

    } catch (err) {
      console.error('Error deleting subtype:', err)
      setError(err.message || 'Failed to delete subtype')
    }
  }

  if (loading) {
    return <div className="loading">Loading...</div>
  }

  return (
    <div className="admin-form-page">
      <h1>Manage Brands & Types</h1>
      <p className="page-subtitle">Manage brands, lure types, and subtypes</p>

      {error && <div className="error-message">❌ {error}</div>}
      {success && <div className="success-message">✅ {success}</div>}

      {/* ============================================
          BRANDS SECTION
          ============================================ */}
      <div className="manage-section">
        <div className="section-header">
          <h2>Brands ({brands.length})</h2>
          <button onClick={handleAddBrand} className="btn-primary">
            ➕ Add Brand
          </button>
        </div>

        {showBrandForm && (
          <div className="inline-form-card">
            <h3>{editingBrand ? 'Edit Brand' : 'Add New Brand'}</h3>
            <form onSubmit={handleSubmitBrand}>
              <div className="form-group">
                <label htmlFor="brand_name">Brand Name *</label>
                <input
                  type="text"
                  id="brand_name"
                  name="brand_name"
                  value={brandFormData.brand_name}
                  onChange={handleBrandFormChange}
                  placeholder="e.g., 13 Fishing"
                  className="form-input"
                  required
                />
              </div>

              <div className="form-group">
                <label htmlFor="website_url">Website URL</label>
                <input
                  type="url"
                  id="website_url"
                  name="website_url"
                  value={brandFormData.website_url}
                  onChange={handleBrandFormChange}
                  placeholder="https://www.example.com"
                  className="form-input"
                />
              </div>

              <div className="form-group">
                <label htmlFor="country">Country</label>
                <input
                  type="text"
                  id="country"
                  name="country"
                  value={brandFormData.country}
                  onChange={handleBrandFormChange}
                  placeholder="e.g., USA, Japan"
                  className="form-input"
                />
              </div>

              <div className="form-group">
                <label htmlFor="description">Description</label>
                <textarea
                  id="description"
                  name="description"
                  value={brandFormData.description}
                  onChange={handleBrandFormChange}
                  placeholder="Describe the brand's history, philosophy, notable products..."
                  className="form-textarea"
                  rows="4"
                ></textarea>
                <small className="field-hint">
                  This will appear on the brand detail page and lure pages
                </small>
              </div>

              <div className="form-actions">
                <button
                  type="button"
                  onClick={() => setShowBrandForm(false)}
                  className="btn-secondary"
                >
                  Cancel
                </button>
                <button type="submit" className="btn-primary">
                  {editingBrand ? 'Update Brand' : 'Add Brand'}
                </button>
              </div>
            </form>
          </div>
        )}

        <div className="items-grid">
          {brands.map(brand => (
            <div key={brand.brand_id} className="item-card">
              <div className="item-card-header">
                <h4>{brand.brand_name}</h4>
              </div>
              {brand.country && (
                <div className="item-detail">
                  <strong>Country:</strong> {brand.country}
                </div>
              )}
              {brand.website_url && (
                <div className="item-detail">
                  <strong>Website:</strong>{' '}
                  <a href={brand.website_url} target="_blank" rel="noopener noreferrer">
                    Link
                  </a>
                </div>
              )}
              {brand.description && (
                <div className="item-description">
                  {brand.description}
                </div>
              )}
              <div className="item-actions">
                <button onClick={() => handleEditBrand(brand)} className="btn-edit">
                  ✏️ Edit
                </button>
                <button onClick={() => handleDeleteBrand(brand)} className="btn-delete">
                  🗑️ Delete
                </button>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* ============================================
          LURE TYPES SECTION
          ============================================ */}
      <div className="manage-section">
        <div className="section-header">
          <h2>Lure Types ({lureTypes.length})</h2>
          <button onClick={handleAddType} className="btn-primary">
            ➕ Add Type
          </button>
        </div>

        {showTypeForm && (
          <div className="inline-form-card">
            <h3>{editingType ? 'Edit Lure Type' : 'Add New Lure Type'}</h3>
            <form onSubmit={handleSubmitType}>
              <div className="form-group">
                <label htmlFor="type_name">Type Name *</label>
                <input
                  type="text"
                  id="type_name"
                  name="type_name"
                  value={typeFormData.type_name}
                  onChange={handleTypeFormChange}
                  placeholder="e.g., Spinnerbait, Swimbait"
                  className="form-input"
                  required
                />
              </div>

              <div className="form-group">
                <label htmlFor="type_description">Description</label>
                <textarea
                  id="type_description"
                  name="description"
                  value={typeFormData.description}
                  onChange={handleTypeFormChange}
                  placeholder="Describe this lure type..."
                  className="form-textarea"
                  rows="3"
                ></textarea>
              </div>

              <div className="form-actions">
                <button
                  type="button"
                  onClick={() => setShowTypeForm(false)}
                  className="btn-secondary"
                >
                  Cancel
                </button>
                <button type="submit" className="btn-primary">
                  {editingType ? 'Update Type' : 'Add Type'}
                </button>
              </div>
            </form>
          </div>
        )}

        <div className="items-grid">
          {lureTypes.map(type => (
            <div key={type.type_id} className="item-card">
              <div className="item-card-header">
                <h4>{type.type_name}</h4>
              </div>
              {type.description && (
                <div className="item-description">{type.description}</div>
              )}
              <div className="item-actions">
                <button onClick={() => handleEditType(type)} className="btn-edit">
                  ✏️ Edit
                </button>
                <button onClick={() => handleDeleteType(type)} className="btn-delete">
                  🗑️ Delete
                </button>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* ============================================
          LURE SUBTYPES SECTION
          ============================================ */}
      <div className="manage-section">
        <div className="section-header">
          <h2>Lure Subtypes ({lureSubtypes.length})</h2>
          <button onClick={handleAddSubtype} className="btn-primary">
            ➕ Add Subtype
          </button>
        </div>

        {showSubtypeForm && (
          <div className="inline-form-card">
            <h3>{editingSubtype ? 'Edit Subtype' : 'Add New Subtype'}</h3>
            <form onSubmit={handleSubmitSubtype}>
              <div className="form-group">
                <label htmlFor="subtype_name">Subtype Name *</label>
                <input
                  type="text"
                  id="subtype_name"
                  name="subtype_name"
                  value={subtypeFormData.subtype_name}
                  onChange={handleSubtypeFormChange}
                  placeholder="e.g., Buzzbait, Glide Bait"
                  className="form-input"
                  required
                />
              </div>

              <div className="form-group">
                <label htmlFor="subtype_description">Description</label>
                <textarea
                  id="subtype_description"
                  name="description"
                  value={subtypeFormData.description}
                  onChange={handleSubtypeFormChange}
                  placeholder="Describe this subtype..."
                  className="form-textarea"
                  rows="3"
                ></textarea>
              </div>

              <div className="form-actions">
                <button
                  type="button"
                  onClick={() => setShowSubtypeForm(false)}
                  className="btn-secondary"
                >
                  Cancel
                </button>
                <button type="submit" className="btn-primary">
                  {editingSubtype ? 'Update Subtype' : 'Add Subtype'}
                </button>
              </div>
            </form>
          </div>
        )}

        <div className="items-grid">
          {lureSubtypes.map(subtype => (
            <div key={subtype.subtype_id} className="item-card">
              <div className="item-card-header">
                <h4>{subtype.subtype_name}</h4>
              </div>
              {subtype.description && (
                <div className="item-description">{subtype.description}</div>
              )}
              <div className="item-actions">
                <button onClick={() => handleEditSubtype(subtype)} className="btn-edit">
                  ✏️ Edit
                </button>
                <button onClick={() => handleDeleteSubtype(subtype)} className="btn-delete">
                  🗑️ Delete
                </button>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  )
}

export default ManageBrands